import { HTTP_METHODS } from "@src/constants/http-methods.constant";
import { INTERNAL_API_ENDPOINTS } from "@src/constants/internal-api-endpoints-constants";
import type { ScheduleAppointmentRequestPayload } from "@src/services/schedule-appointment.service";
import type { ApiCallConfig } from "@src/utils/api-client";
import {
  normalizeAppointmentTypeId,
  resolveInsurancePackageId,
  sanitizePayerId,
} from "@src/utils/schedule-appointment-utils";
import { normalizeEmail } from "@src/utils/string-utils";

/**
 * Builds the callApi config for the schedule-appointment endpoint.
 * Applies all body transformations:
 *   - trims patient_email
 *   - maps self_pay → insurance_package_id=0
 *   - normalises appointment_type_id to an integer
 *   - sanitizes payer_id (strips non-positive integers and invalid strings)
 *   - strips undefined fields
 *
 * Validation (type_name in allowed list, type_id is a positive integer) is the
 * caller's responsibility and must be performed before calling this function.
 */
export function buildScheduleAppointmentConfig(
  payload: ScheduleAppointmentRequestPayload,
  signal?: AbortSignal,
): ApiCallConfig {
  const body: Record<string, unknown> = {
    ...payload,
    patient_email: normalizeEmail(payload.patient_email),
    insurance_package_id: resolveInsurancePackageId(payload.self_pay, payload.insurance_package_id),
    appointment_type_id: normalizeAppointmentTypeId(payload.appointment_type_id),
    payer_id: payload.self_pay ? undefined : sanitizePayerId(payload.payer_id),
  };

  for (const key of Object.keys(body)) {
    if (body[key] === undefined) {
      delete body[key];
    }
  }

  return {
    method: HTTP_METHODS.POST,
    endpoint: INTERNAL_API_ENDPOINTS.SCHEDULE_APPOINTMENT,
    body,
    ...(signal ? { signal } : {}),
  };
}
